import { iaRa } from 'lib/helpers/utility';

const HEALTH_SUMMARY_KEY = 'healthSummary';

// Rows returned by /api/health_summary/get carry `createdBy` but not the derived
// `createdBy.isPatient` flag that the UI relies on (patient-vs-provider tab split in
// PreviousFormResponses, patient-response badge icons in the table columns, and edit gating via
// isDataReadOnly). Optimistically-created rows get it from the form hooks' `decorate`, but
// server-fetched rows do not — so derive it once, here, on every list slice of the payload.
// Canonical derivation everywhere: the author is the patient when createdBy.id === patientId.
export const enrichCreatedByIsPatient = (response, patientId) => {
  if (!response || typeof response !== 'object') return response;
  const target = Number(patientId);

  const enrichRow = (row) => {
    if (!row?.createdBy || row.createdBy.id == null) return row;
    const isPatient = Number(row.createdBy.id) === target;
    if (row.createdBy.isPatient === isPatient) return row;
    return { ...row, createdBy: { ...row.createdBy, isPatient } };
  };

  let changed = false;
  const next = {};
  for (const [key, value] of Object.entries(response)) {
    if (Array.isArray(value)) {
      const mapped = value.map(enrichRow);
      if (mapped.some((r, i) => r !== value[i])) changed = true;
      next[key] = mapped;
    } else {
      next[key] = value;
    }
  }
  return changed ? next : response;
};

const matchesPatient = (queryKey, patientId) => {
  if (!Array.isArray(queryKey) || queryKey[0] !== HEALTH_SUMMARY_KEY) return false;
  return String(queryKey[1]) === String(patientId);
};

export const patchHealthSummarySlice = ({ queryClient, patientId, slice, updater }) => {
  if (!queryClient || !patientId || !slice || typeof updater !== 'function') return;

  queryClient.setQueriesData(
    { predicate: (query) => matchesPatient(query.queryKey, patientId) },
    (prev) => {
      if (!prev?.response) return prev;
      const current = iaRa(prev.response[slice]);
      const next = updater(current);
      if (next === current) return prev;
      return { ...prev, response: { ...prev.response, [slice]: next } };
    }
  );
  invalidateHealthSummaryForPatient({ queryClient, patientId, refetchType: 'none' });
};

export const upsertHealthSummaryRows = ({ queryClient, patientId, slice, rows, decorate }) => {
  const incoming = iaRa(rows);
  if (!incoming.length) return;

  patchHealthSummarySlice({
    queryClient,
    patientId,
    slice,
    updater: (existing) => {
      const upsertedIds = new Set(incoming.map((r) => r.id));
      const existingById = new Map(existing.map((r) => [r.id, r]));
      const filtered = existing.filter((r) => !upsertedIds.has(r.id));
      const decorated = decorate
        ? incoming.map((r) => decorate(r, existingById.get(r.id)))
        : incoming;
      return [...decorated, ...filtered];
    }
  });
};

export const invalidateHealthSummaryForPatient = ({ queryClient, patientId, refetchType }) => {
  if (!queryClient || !patientId) return;
  queryClient.invalidateQueries({
    predicate: (query) => matchesPatient(query.queryKey, patientId),
    ...(refetchType && { refetchType })
  });
};

// Create endpoints already pin their rows to the (unlocked) note's aggregate data but return them
// without `inMhad`; stamp it the way health_summary/get does (`mhad.resource_id as inMhad`) so the
// note's save flow doesn't treat them as unsaved and re-upsert them.
export const markPinnedToNote = (rows) => iaRa(rows).map((row) => ({ ...row, inMhad: row.id }));

export const upsertPatientListCache = ({ queryClient, queryKeyPrefix, patientId, rows }) => {
  const incoming = iaRa(rows).filter((r) => r?.id != null);
  if (!queryClient || !queryKeyPrefix || patientId == null || !incoming.length) return;

  queryClient.setQueriesData(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === queryKeyPrefix &&
        String(query.queryKey[1]) === String(patientId)
    },
    (prev) => {
      const list = Array.isArray(prev?.data) ? prev.data : [];
      const upsertedIds = new Set(incoming.map((r) => r.id));
      const filtered = list.filter((r) => !upsertedIds.has(r?.id));
      const merged = [...incoming, ...filtered];
      return prev ? { ...prev, data: merged } : { data: merged };
    }
  );
  queryClient.invalidateQueries({ queryKey: [queryKeyPrefix], refetchType: 'none' });
};

export const upsertOverviewDataSlice = ({ setOverviewData, slice, rows }) => {
  const incoming = iaRa(rows).filter((r) => r?.id != null);
  if (typeof setOverviewData !== 'function' || !slice || !incoming.length) return;

  setOverviewData((prev) => {
    const existing = iaRa(prev?.[slice]);
    const upsertedIds = new Set(incoming.map((r) => r.id));
    const filtered = existing.filter((r) => !upsertedIds.has(r?.id));
    return { ...(prev || {}), [slice]: [...incoming, ...filtered] };
  });
};

export const removeHealthSummaryRow = ({ queryClient, patientId, slice, id }) => {
  if (id == null) return;

  patchHealthSummarySlice({
    queryClient,
    patientId,
    slice,
    updater: (existing) => existing.filter((r) => r.id !== id)
  });
};
